import { IAuditLogEdit, IEditPermissivenessLevel, ILog, ILogEditableField, ILogFieldEditability, Log } from "../../Types"

/**
 * Client-side mirror of `Sources/LogbookModel/EditPermissiveness.swift` (issue #529).
 * The server enforces this same rule on `logs.update`; the client reimplements it
 * (rather than the server sending precomputed `editable`/`locked` flags) so it has
 * everything it needs to render fields read-only *before* a rejected save round-trips
 * — @mutle: "the client will already have all the necessary information available".
 * Keep this in sync by hand with the Swift source of truth whenever it changes.
 */

// Mirrors `LogEditableField.editability` (Sources/LogbookModel/EditPermissiveness.swift).
export const LOG_FIELD_EDITABILITY: Record<ILogEditableField, ILogFieldEditability> = {
  [ILogEditableField.depth]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.engineRpms]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.heading]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.humidity]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.log]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.pressure]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.stw]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.temperature]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.windDirection]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.windSpeed]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.course]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.sog]: ILogFieldEditability.nmeaCapable,
  [ILogEditableField.distance]: ILogFieldEditability.locked,
  [ILogEditableField.comment]: ILogFieldEditability.always,
  [ILogEditableField.currentDirection]: ILogFieldEditability.always,
  [ILogEditableField.currentSpeed]: ILogFieldEditability.always,
  [ILogEditableField.location]: ILogFieldEditability.always,
  [ILogEditableField.seaState]: ILogFieldEditability.always,
  [ILogEditableField.watch]: ILogFieldEditability.always,
  [ILogEditableField.watchComment]: ILogFieldEditability.always,
  [ILogEditableField.weather]: ILogFieldEditability.always,
};

// Mirrors `EditPermissivenessLevel.logEditableWindow` (same file): how long, after
// becoming the most recent log of its kind, a `.nmeaCapable` field other than manual
// RPM stays editable at `.strict`.
export const LOG_EDITABLE_WINDOW_SECONDS = 3600;

/**
 * Mirrors `EditPermissivenessLevel.allowsLogFieldEdit`. `nmeaProtected` and
 * `withinEditableWindow` are irrelevant for `.always` (always allowed) and `.locked`
 * (never allowed) fields. RPM ignores the window, but retains NMEA protection.
 */
export function allowsLogFieldEdit(field: ILogEditableField, level: IEditPermissivenessLevel, nmeaProtected: boolean, withinEditableWindow: boolean): boolean {
  if (LOG_FIELD_EDITABILITY[field] == ILogFieldEditability.always) {
    return true;
  }
  if (LOG_FIELD_EDITABILITY[field] == ILogFieldEditability.locked) {
    return false;
  }
  switch (level) {
    case IEditPermissivenessLevel.strict:
      return (field == ILogEditableField.engineRpms || withinEditableWindow) && !nmeaProtected;
    case IEditPermissivenessLevel.extended:
      return !nmeaProtected;
    case IEditPermissivenessLevel.unrestricted:
      return true;
  }
}

// Mirrors `logs.update`'s `isMostRecentOfKind` (Sources/LogbookLocal/LogApi.swift):
// day-scoped, matched by `metadata.nmeaAuto` (manual vs. NMEA-automatic).
export function isMostRecentLogOfKind(log: ILog, logs: ILog[]): boolean {
  const nmeaAuto = Log.parse(log).dataParsed.nmeaAuto || false;
  return !logs.some((sibling) => sibling.id > log.id && (Log.parse(sibling).dataParsed.nmeaAuto || false) == nmeaAuto);
}

/**
 * Mirrors `logs.update`'s `withinEditableWindow` computation. `forceEditable` bypasses
 * only the age check (not the most-recent-of-kind check) — used by the LogView
 * "editable" catalog fixture, whose fixture data can't have a "now" timestamp; mirrors
 * the pre-existing `app.forceLogEditable` catalog-only override.
 */
export function isWithinEditableWindow(log: ILog, logs: ILog[], now: number, forceEditable = false): boolean {
  return isMostRecentLogOfKind(log, logs) && (forceEditable || (now - log.timeUtc) < LOG_EDITABLE_WINDOW_SECONDS);
}

/**
 * Mirrors `logs.update`'s `nmeaProtected` computation: `field` is still NMEA-protected
 * while it's `.nmeaCapable`, was recorded as NMEA-supplied on this specific log
 * (`metadata.nmeaFields`, captured at log-creation time — NOT whether NMEA is
 * currently live/connected), and has never been manually superseded before (no
 * `AuditLogEdit` row for this log+field yet).
 */
export function isNmeaProtected(field: ILogEditableField, log: ILog, auditLogEdits: IAuditLogEdit[]): boolean {
  if (LOG_FIELD_EDITABILITY[field] != ILogFieldEditability.nmeaCapable) {
    return false;
  }
  const nmeaFields = Log.parse(log).dataParsed.nmeaFields || [];
  if (nmeaFields.indexOf(field) < 0) {
    return false;
  }
  return !auditLogEdits.some((edit) => edit.logId == log.id && edit.field == field);
}

/**
 * The single entry point views should use: whether `field` on `log` is currently
 * editable, given the day's sibling `logs` (for the most-recent-of-kind check), the
 * day's `auditLogEdits` (for the previously-edited exemption) and the ship's
 * `level`. `now` is `Time.timeUtc()`-shaped (seconds), taken as a parameter to keep
 * this function pure/testable.
 */
export function logFieldEditable(field: ILogEditableField, log: ILog, logs: ILog[], auditLogEdits: IAuditLogEdit[], level: IEditPermissivenessLevel, now: number, forceEditable = false): boolean {
  const nmeaProtected = isNmeaProtected(field, log, auditLogEdits);
  const withinEditableWindow = isWithinEditableWindow(log, logs, now, forceEditable);
  return allowsLogFieldEdit(field, level, nmeaProtected, withinEditableWindow);
}

// Mirrors `EditPermissivenessLevel.editHistoryVisibility` (Sources/LogbookModel/EditPermissiveness.swift, issue #1169):
// whether the Edit History UI is shown to a ship at each level. A code-level
// constant (not a ship setting) so it stays trivially easy to change later.
export const EDIT_HISTORY_VISIBLE: Record<IEditPermissivenessLevel, boolean> = {
  [IEditPermissivenessLevel.strict]: true,
  [IEditPermissivenessLevel.extended]: true,
  [IEditPermissivenessLevel.unrestricted]: false,
};

// Mirrors `EditPermissivenessLevel.showsEditHistory`.
export function showsEditHistory(level: IEditPermissivenessLevel): boolean {
  return EDIT_HISTORY_VISIBLE[level] ?? true;
}
